import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Box } from '../common';
import { colors } from '@styles/theme';

import clamp from 'lodash-es/clamp';
import { useSpring, animated, interpolate } from 'react-spring';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import useDetectSize from '@hooks/useDetectSize';
import { useDrag } from 'react-use-gesture';
import DragActions from '@redux/DragRedux';
import SvgIcon from '../SvgIcon';

const DragWrapper = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: column;

  cursor: -webkit-grabbing;

  @media screen and (min-width: 1024px) {
    width: 88px;
    height: 88px;
    min-height: 88px;
    justify-content: flex-start;
    align-items: flex-start;
  }
  @media screen and (min-width: 768px) and (max-width: 1023px) {
    width: 64px;
    height: 64px;
    min-height: 64px;
    justify-content: flex-start;
    align-items: flex-start;
  }
  @media screen and (max-width: 767px) {
    width: 48px;
    height: 48px;
    min-height: 48px;
    justify-content: center;
    align-items: center;
  }
`;

const Wrapper = styled(animated.div)`
  min-width: 32px;
  min-height: 32px;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  background: ${props => (props.disabled ? colors.lightGray : 'white')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 5px;
  position: relative;
  box-shadow: 0px 2px 24px 0px rgba(148, 102, 41, 0.1),
    0px 4px 8px 0px rgba(148, 102, 41, 0.1),
    0px 2px 4px 0px rgba(148, 102, 41, 0.15);
`;

function DraggableCalendarCell(props) {
  const {
    icon,
    onDrop,
    disabled,
    color,
    style,
    setDraggingStarted,
    setDraggingStopped,
    ...restProps
  } = props;

  const iconProps = {
    width: 16,
    height: 16,
    color
  };

  const [animStarted, setAnimStarted] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const refDragContainer = useRef();
  const refDragElement = useRef();
  const isMobile = useDetectSize();

  const appElement = document.getElementById('#root');

  const dragType = 'clone';

  const [{ xy, scale, op, shadow }, set] = useSpring(() => ({
    xy: [0, 0],
    scale: 1,
    shadow: 3,
    op: 1
  }));
  const bind = useDrag(
    ({ event, down, first, last, delta, velocity }) => {
      let currentOffset = null;
      if (disabled) {
        return;
      }
      if (first) {
        setAnimStarted(true);
        if (dragType) {
          setDraggingStarted(dragType);
        }
        if (isMobile) {
          disableBodyScroll(appElement);
        }

        if (refDragElement.current) {
          const { clientX, clientY } = event.changedTouches
            ? event.changedTouches[0]
            : event;
          const elementSize = refDragElement.current.getBoundingClientRect();

          currentOffset = {
            x: clientX - (elementSize.left + elementSize.width / 2),
            y: clientY - (elementSize.top + elementSize.height / 2)
          };
        }

        setOffset(currentOffset);
      }

      if (last) {
        setDraggingStopped();
        if (isMobile) {
          enableBodyScroll(appElement);
        }
        setOffset({
          x: 0,
          y: 0
        });
      }

      velocity = clamp(velocity, 1, 8);
      if (!down && onDrop) {
        let element = null;
        const { clientX, clientY } = event.changedTouches
          ? event.changedTouches[0]
          : event;

        element = document
          .elementsFromPoint(clientX, clientY)
          .find(
            item =>
              item.className &&
              item.className.includes &&
              (item.className.includes('schedule-button') ||
                item.className.includes('drag-remove-container'))
          );

        if (element && element.className.includes('drag-remove-container')) {
          set({
            xy: [0, 0],
            scale: 1,
            op: 1,
            shadow: 3,
            config: { mass: velocity, tension: 500 * velocity, friction: 50 }
          });

          onDrop(-1, -1);
          setDraggingStopped();
          setAnimStarted(false);
          return;
        }

        if (
          element &&
          !element.className.includes('schedule-button-disabled') &&
          !element.className.includes('schedule-button-filled')
        ) {
          const rowId = element.getAttribute('data-row-id');
          const colId = element.getAttribute('data-col-id');

          set({
            xy: [0, 0],
            scale: 1,
            op: 1,
            shadow: 3,
            config: { mass: velocity, tension: 500 * velocity, friction: 50 }
          });

          setDraggingStopped();
          onDrop(rowId, colId);
          setAnimStarted(false);
          return;
        }
      }

      const targetOffset = currentOffset ? currentOffset : offset;

      set({
        xy: down
          ? [delta[0] + targetOffset.x, delta[1] + targetOffset.y]
          : [0, 0],
        scale: down ? 2.5 : 1,
        op: down ? 0.8 : 1,
        shadow: down ? 8 : 3,
        config: { mass: velocity, tension: 500 * velocity, friction: 50 }
      });
    },
    {
      domTarget: refDragContainer.current,
      event: { passive: true, capture: false },
      drag: true,
      pinch: false,
      scroll: false,
      wheel: false,
      move: false
    }
  );

  useEffect(() => bind, [bind]);

  return (
    <DragWrapper
      disabled={disabled}
      ref={refDragContainer}
      {...(!disabled ? bind() : null)}
      {...restProps}
    >
      <Wrapper
        ref={refDragElement}
        disabled={disabled}
        style={
          animStarted
            ? {
                transform: interpolate(
                  [xy, scale],
                  ([x, y], s) => `translate3d(${x}px,${y}px,0) scale(${s})`
                ),
                boxShadow: shadow.interpolate(
                  s => `rgba(0, 0, 0, 0.2) 0px ${s}px ${2 * s}px 0px`
                ),
                opacity: op,
                zIndex: 9999,
                ...style
              }
            : { zIndex: 0, ...style }
        }
      >
        <SvgIcon name={icon} {...iconProps} />
      </Wrapper>
    </DragWrapper>
  );
}

DraggableCalendarCell.propTypes = {
  icon: PropTypes.string,
  onDrop: PropTypes.func,
  setDraggingStarted: PropTypes.func,
  setDraggingStopped: PropTypes.func,
  disabled: PropTypes.bool,
  dragType: PropTypes.string,
  color: PropTypes.any,
  style: PropTypes.any
};

const mapDispatchToProps = dispatch => ({
  setDraggingStarted: type => dispatch(DragActions.setDragging(type)),
  setDraggingStopped: () => dispatch(DragActions.setDragging(null))
});

export default connect(
  null,
  mapDispatchToProps
)(DraggableCalendarCell);
